/**
 * OidcCallback.tsx — landing page for the Entra OIDC redirect (SPEC §2, server
 * handlers_oidc.go).
 *
 * The server completes the code exchange + multi-tenant id_token verification,
 * then 302s the browser to cfg.OIDCConsoleCallback (this route) with the result
 * in the URL FRAGMENT — never the query string — so tokens never reach server
 * logs or the Referer header:
 *
 *   success:   /auth/oidc-callback#access=<jwt>&refresh=<jwt>
 *   enroll:    /auth/oidc-callback#register=<registration_token>
 *   failure:   /auth/oidc-callback#error=org_not_provisioned
 *
 * On success we hand the tokens to the auth store (same path as password login)
 * and continue to /devices.
 *
 * On `#register=<token>` the OIDC'd user is a zero-credential account (no passkey)
 * and the deployment does NOT allow OIDC as a sole factor: WebAuthn/FIDO2 is
 * mandatory (SPEC §2 / §11.6), so the server withholds console tokens and instead
 * hands back a short-lived, registration-ONLY token. The console must enroll a
 * first passkey NOW with that token before any session tokens are issued — this
 * mirrors the password-path first-run flow in Login.tsx (enrollFirstPasskey).
 *
 * On failure we show a human-readable message.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/api/provider';
import { useAuthStore } from '@/auth/store';
import { createCredential, webauthnSupported } from '@/auth/webauthn';
import { ApiError } from '@/api/client';

/** Map known server error codes to operator-facing copy. */
function describeError(code: string): string {
  switch (code) {
    case 'org_not_provisioned':
      return "Your organization isn't provisioned for Helm — contact your administrator.";
    default:
      return 'Sign-in with Microsoft failed. Please try again or contact your administrator.';
  }
}

/** Turn an enrollment failure into operator-facing copy (mirrors Login.humanize). */
function humanizeEnroll(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof DOMException && err.name === 'NotAllowedError') {
    return 'Security-key prompt was dismissed or timed out. Try again.';
  }
  if (err instanceof Error) return err.message;
  return 'Passkey enrollment failed.';
}

export default function OidcCallback() {
  const api = useApi();
  const setTokens = useAuthStore((s) => s.setTokens);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // While we run the (async) first-passkey ceremony, show a distinct status.
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    // The fragment leads with '#'; URLSearchParams wants it stripped.
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);

    const errCode = params.get('error');
    if (errCode) {
      setError(describeError(errCode));
      return;
    }

    const access = params.get('access');
    const refresh = params.get('refresh');
    if (access && refresh) {
      setTokens({ access, refresh });
      navigate('/devices', { replace: true });
      return;
    }

    // Zero-credential OIDC user when sole-factor isn't allowed: the server returns
    // a registration-ONLY token instead of console tokens. Enroll a passkey NOW
    // with that token; register/finish returns the full console tokens (the
    // register token is never persisted as a session token). Same ceremony as the
    // password-path first run in Login.tsx (enrollFirstPasskey).
    const registerToken = params.get('register');
    if (registerToken) {
      let cancelled = false;
      (async () => {
        setEnrolling(true);
        try {
          if (!webauthnSupported()) {
            throw new Error(
              'This browser does not support WebAuthn. A passkey or security key is required to finish setting up your account.',
            );
          }
          const begin = await api.webauthnRegisterBegin(registerToken);
          const credential = await createCredential(begin.public_key.publicKey);
          const res = await api.webauthnRegisterFinish(
            begin.challenge_id,
            credential,
            registerToken,
          );
          if (!res.access || !res.refresh) {
            throw new Error(
              'Passkey registered, but the server did not return a session. Please sign in again.',
            );
          }
          if (cancelled) return;
          setTokens({ access: res.access, refresh: res.refresh });
          navigate('/devices', { replace: true });
        } catch (err) {
          if (!cancelled) setError(humanizeEnroll(err));
        } finally {
          if (!cancelled) setEnrolling(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // Neither tokens, a register token, nor a recognized error — treat as a
    // failed/garbled callback.
    setError(describeError('unknown'));
  }, [api, navigate, setTokens]);

  return (
    <div className="min-h-screen grid place-items-center bg-helm-bg p-4">
      <div className="w-full max-w-sm rounded-xl border border-helm-border bg-helm-panel p-6 text-center shadow-xl">
        <div className="mb-4 text-2xl font-semibold text-helm-text">Helm</div>
        {error ? (
          <>
            <div
              role="alert"
              className="mb-4 rounded-md border border-helm-bad/40 bg-helm-bad/10 px-3 py-2 text-sm text-helm-bad"
            >
              {error}
            </div>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="w-full rounded-md bg-helm-accent px-3 py-2 font-medium text-white transition hover:bg-helm-accentDim"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <div className="text-sm text-helm-muted">
            {enrolling ? 'Set up your passkey to finish signing in…' : 'Completing sign-in…'}
          </div>
        )}
      </div>
    </div>
  );
}
